import { usePropertyStore } from '@/common/stores/property';
import { EvMessage } from 'evui';
import dayjs from 'dayjs';
import { TIMEZONE_ITEMS } from '@/common/utils/define';
import { setHeaderConfig } from '@/worker/commands/config/apiInstance';
import { getUtcOffset } from '@/common/utils/commonUtils';
import { i18n } from '@/common/locale';
import { useAuthStore } from '@/common/stores/auth';
import { storeToRefs } from 'pinia';
import { useRolePermissionStore } from '@/config/stores/rolePermission';
import { useViewManagerStore } from '@/common/stores/view-manager';
import { useStatInfoStore } from '@/common/stores/stat-info';
import { RouteLocationNormalized } from 'vue-router';
import { checkRole, isRoleIdIncluded } from '@/common/auth/auth.utils';
import {
  checkAllPermissionDenied,
  findPermissionByTranslateId,
} from '@/common/permission/permission.utils';
import { useDatabaseMetaStore } from '@/database/stores/database-meta';
import { validateDatabaseOverviewRoute } from '@/database/utils/path.util';
import { getInstancesInfo } from '@/database/utils/fetch.utils';
import {
  prefetchCommonImages,
  prefetchCustomEnv,
  prefetchInstanceCount,
  prefetchStatInfo,
  prefetchUserEnv,
  prefetchDashboardUserEnv,
  prefetchPartialDashboardUserEnv,
} from '@/common/lib/prefetch';
import { queryClient } from '@/common/lib/queryClient';
import { VIEW_MODE } from '@/common/define/common.define';
import {
  getGetV7KubernetesClustersOpenshiftExistsQueryKey,
  type GetV7KubernetesClustersOpenshiftExistsQueryResult,
} from '@/api-client/kubernetes/queries/kubernetes-cluster-v7-controller/kubernetes-cluster-v7-controller';
import { getGetV8TargetIconInitQueryKey } from '@/api-client/icon/queries/target-icon-controller/target-icon-controller';
import { getGetV8DashboardsWidgetsCustomImagesQueryKey } from '@/api-client/metav8Over/queries/widget-custom-image-controller/widget-custom-image-controller';
import {
  getGetV11TargetByCategoryCountQueryKey,
  getV11TargetByCategoryCount,
} from '@/api-client/target/queries/target-status-controller/target-status-controller';
import { useViewModeStore } from '../stores/view-mode';
import { useUserEnvStore } from '../stores/user-env';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useDashboardUserEnvStore } from '@/dashboard/stores/dashboard-user-env';
import { isDashboardRoute } from './router.utils';

export const PATH = {
  ROOT: '/',
  LOGOUT: '/logout',
  HOME: '/home',
  DASHBOARD_LIST: '/dashboard/list',
  AUTH_FAIL: '/401',
  NOT_FOUND: '/404',
  FORBIDDEN: '/403',
  PUBLIC_POPUP: '/public-popup',
  POPUP: '/popup',
};

const showErrorMsg = (msg: string) => {
  EvMessage({
    type: 'error',
    message: msg,
  });
};

const ControlPageLoading = {
  active: () => {
    const { isLoadingRouterForEach } = storeToRefs(useViewManagerStore());
    isLoadingRouterForEach.value = true;
  },
  inactive: () => {
    const { isLoadingRouterForEach } = storeToRefs(useViewManagerStore());
    isLoadingRouterForEach.value = false;
  },
};

const validateToken = async () => {
  const myInfoStore = useMyInfoStore();
  const { myInfo } = storeToRefs(myInfoStore);
  // 토큰 검증 로직
  // -> 화면 api요청 시 사용하는 토큰 기반으로 검증
  try {
    let { userId } = myInfo.value;
    if (userId === -1) {
      ControlPageLoading.active();
      await myInfoStore.dispatchMyInfo();
      userId = myInfo.value.userId;
    }
    return !!userId;
  } catch {
    return false;
  }
};

// 로그인 auth 확인
export const requireAuth = async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore();
  await authStore.setInitialInfo();
  const { isEsLogEnabled } = storeToRefs(usePropertyStore());

  const viewModeStore = useViewModeStore();

  // 토큰 검증
  const hasAuth = authStore.getToken('accessToken');
  if (!hasAuth) {
    showErrorMsg(i18n.global.t('NOTI.PRODUCT.RE_LOGIN'));
    return PATH.LOGOUT;
  }

  // 사용자 정보 검증
  const isValidToken = await validateToken();
  if (!isValidToken) {
    showErrorMsg(i18n.global.t('NOTI.PRODUCT.RE_LOGIN'));
    return PATH.LOGOUT;
  }

  // 비밀번호 변경 필요 여부 검증
  const { isValidPasswordByStorage } = authStore;
  if (isValidPasswordByStorage()) {
    showErrorMsg(i18n.global.t('NOTI.PRODUCT.RE_LOGIN'));
    return PATH.LOGOUT;
  }

  // 병렬 prefetch를 위한 조건 체크
  const userEnvStore = useUserEnvStore();
  const { hasStatInfo } = storeToRefs(useStatInfoStore());
  const targetIconData = queryClient.getQueryData(getGetV8TargetIconInitQueryKey());
  const dashboardImagesData = queryClient.getQueryData(
    getGetV8DashboardsWidgetsCustomImagesQueryKey(),
  );
  const customEnvData = queryClient.getQueryData(
    getGetV7KubernetesClustersOpenshiftExistsQueryKey(),
  );

  const { role: roleId, rolePermissionId } = useMyInfoStore().myInfo;
  const rolePermissionStore = useRolePermissionStore();
  const { setGlobalPermissionList } = rolePermissionStore;

  const dashboardUserEnvStore = useDashboardUserEnvStore();

  // 로딩 없이 항상 실행할 prefetch
  const silentPrefetchPromises: Promise<void>[] = [
    prefetchInstanceCount(),
    setGlobalPermissionList(rolePermissionId),
  ];

  // 로딩이 필요한 prefetch (조건부)
  const loadingPrefetchPromises: Promise<void>[] = [];

  if (!userEnvStore.hasUserEnvInfo) {
    loadingPrefetchPromises.push(prefetchUserEnv());
  }

  if (isDashboardRoute(to) && to.params?.id != null) {
    if (!dashboardUserEnvStore.hasUserEnvInfo) {
      silentPrefetchPromises.push(prefetchDashboardUserEnv(+to.params?.id));
    } else {
      silentPrefetchPromises.push(prefetchPartialDashboardUserEnv(to));
    }
  }

  if (!hasStatInfo.value) {
    loadingPrefetchPromises.push(prefetchStatInfo());
  }
  if (targetIconData === undefined || dashboardImagesData === undefined) {
    loadingPrefetchPromises.push(prefetchCommonImages());
  }
  if (customEnvData === undefined) {
    loadingPrefetchPromises.push(prefetchCustomEnv());
  }

  if (loadingPrefetchPromises.length > 0) {
    ControlPageLoading.active();
  }

  // 개별 prefetch 실패가 라우팅을 차단하지 않도록 allSettled 사용
  await Promise.allSettled([...silentPrefetchPromises, ...loadingPrefetchPromises]);

  const utcOffsetConfig = { key: 'UtcOffset', value: getUtcOffset() };
  setHeaderConfig([utcOffsetConfig]);

  if (to.meta.invisibleViewMode?.includes(viewModeStore.viewMode)) {
    return PATH.NOT_FOUND;
  }

  if (to.meta.requiresESLog && !isEsLogEnabled.value) {
    return PATH.NOT_FOUND;
  }

  // Master 역할은 rolePermissionId와 무관하게 메뉴/화면 권한 검사 통과
  const isPermissionDenied =
    !checkRole.isMaster(roleId) &&
    ((to.meta.rolePermission &&
      (findPermissionByTranslateId(to.meta.rolePermission)?.menuOnOff ??
        findPermissionByTranslateId(to.meta.rolePermission)?.listOnOff) === false) ||
      (to.meta.childRolePermission && checkAllPermissionDenied(to.meta.childRolePermission)));

  if ((to.meta.roles && !isRoleIdIncluded(to.meta.roles, roleId)) || isPermissionDenied) {
    return PATH.FORBIDDEN;
  }

  return undefined;
};

// 최초 접속 및 refresh 시 sessionStorage에 timezone이 있다면 dayjs의 tz를 세팅해줌
export const setDayjsTimezone = () => {
  const { timezone } = useMyInfoStore();
  if (timezone) {
    const timezoneId = TIMEZONE_ITEMS.find((v) => v.value === timezone)?.value;
    if (timezoneId) {
      dayjs.tz.setDefault(timezoneId);
    }
  }
};

export const requireInstances = async () => {
  const { folders, instances } = await getInstancesInfo({ dataType: 'all' });

  useDatabaseMetaStore().setInstanceGroups(folders);
  useDatabaseMetaStore().setInstances(instances);
};

// TutorialTargetKey는 URL prefix와 동일 (예: 'message-queue' → /message-queue/intro)
export type TutorialTargetKey =
  | 'database'
  | 'application'
  | 'kubernetes'
  | 'infrastructure'
  | 'cloud'
  | 'message-queue';

// URL prefix → API 응답 키 매핑 (URL과 API 키가 다른 경우만 명시)
const CATEGORY_API_KEY: Partial<Record<TutorialTargetKey, string>> = {
  'message-queue': 'messageQueue',
};

const resolveCategoryTargetCount = (data: unknown, category: TutorialTargetKey): number => {
  if (!data || typeof data !== 'object') return 0;
  const record = data as Record<string, unknown>;
  const apiKey = CATEGORY_API_KEY[category] ?? category;
  const categoryObj = record[apiKey];
  if (!categoryObj || typeof categoryObj !== 'object') return 0;
  const counts = Object.values(categoryObj as Record<string, unknown>).reduce<number>(
    (acc, value) => acc + (typeof value === 'number' ? value : 0),
    0,
  );
  return counts;
};

// 세션 내 타겟 존재 확인된 카테고리 (페이지 새로고침 시 자동 초기화)
const confirmedCategories = new Set<TutorialTargetKey>();

/**
 * 도메인 페이지 진입 시 해당 도메인 타겟이 0개면 소개 페이지로 이동.
 * (예: /infrastructure/host → 타겟 없으면 /infrastructure/intro)
 * - 이미 타겟 존재 확인된 카테고리는 API 호출 없이 통과
 * - 미확인 카테고리만 API 호출 후 결과에 따라 처리
 * - 새로고침 시 confirmedCategories 초기화 → 재확인
 */
export const requireDomainTargetOrTutorial = async (
  category: TutorialTargetKey,
): Promise<string | undefined> => {
  try {
    const viewModeStore = useViewModeStore();
    if (viewModeStore.viewMode !== VIEW_MODE.EXEMONE) {
      return undefined;
    }

    // 이미 타겟 있음이 확인된 카테고리 → 바로 통과
    if (confirmedCategories.has(category)) {
      return undefined;
    }

    const queryKey = getGetV11TargetByCategoryCountQueryKey();
    const cached = queryClient.getQueryData(queryKey);

    const res =
      cached ??
      (await queryClient.fetchQuery({
        queryKey,
        queryFn: ({ signal }) => getV11TargetByCategoryCount(undefined, signal),
      }));

    const first = (res as { data?: { data?: unknown[] } } | undefined)?.data?.data?.[0];
    const count = resolveCategoryTargetCount(first, category);
    if (count > 0) {
      confirmedCategories.add(category);
      return undefined;
    }

    EvMessage({
      type: 'warning',
      message: i18n.global.t('MESSAGE.NO_REGISTERED_TARGET'),
    });

    return `/${category}/intro`;
  } catch {
    // API 실패 시에는 기존 라우팅을 막지 않음
    return undefined;
  }
};

/**
 * 타겟 등록 후 해당 카테고리의 확인 상태를 초기화한다.
 * 이후 해당 도메인 진입 시 API를 재호출하여 최신 상태를 반영한다.
 */
export const invalidateDomainTargetCache = (category: TutorialTargetKey) => {
  confirmedCategories.delete(category);
  queryClient.invalidateQueries({ queryKey: getGetV11TargetByCategoryCountQueryKey() });
};

// OpenShift 클러스터 부재 시 관련 화면 진입 차단 → 404 (메뉴 트리의 openshift 노출 조건과 동일 기준)
// requireAuth의 prefetchCustomEnv 이후 실행 필요 (beforeEnter에서 requireAuth 다음에 등록)
export const requireOpenShiftCluster = () => {
  const openShiftExistsRes =
    queryClient.getQueryData<GetV7KubernetesClustersOpenshiftExistsQueryResult>(
      getGetV7KubernetesClustersOpenshiftExistsQueryKey(),
    );
  const exists = openShiftExistsRes?.data?.data ?? false;
  return exists ? undefined : PATH.NOT_FOUND;
};

export const validateDatabaseOverviewPath = (to: RouteLocationNormalized) => {
  return validateDatabaseOverviewRoute(to).then((path) => path);
};
